import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, type Variants } from 'framer-motion';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Sparkles,
  ArrowRight,
  CheckCircle,
  Bell,
  Brain,
  Lock,
  Zap
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { HoverLift, GradientShift, PulseGlow } from '../ui/motion-primitives';
import { MultimodalScene } from './MultimodalVisuals';
import { motionDurations, motionEasings, motionStagger, motionViewport } from '@/lib/motion-tokens';

// Types
interface MultimodalFeature {
  id: string;
  name: string;
  description: string;
  icon: string;
  emoji?: string;
  
  // Timeline
  releaseQuarter?: string;
  releaseDate?: string;
  status: FeatureStatus;
  progressPercentage?: number;
  
  // Feature Details
  capabilities: string[];
  useCases?: string[];
  
  // Marketing
  earlyAccessUrl?: string;
  demoUrl?: string;
  blogPostUrl?: string;
  
  // Styling
  gradientFrom: string;
  gradientTo: string;
  borderColor: string;
  accentColor: string;
}

type FeatureStatus = 
  | 'concept' 
  | 'design' 
  | 'development' 
  | 'beta' 
  | 'launched';

interface CTAButton {
  text: string;
  href: string;
  onClick?: () => void;
}

interface MultimodalPreviewProps {
  // Content Props
  title?: string;
  subtitle?: string;
  badgeText?: string;
  
  // Feature Props
  features?: MultimodalFeature[];
  showReleaseTimeline?: boolean;
  showFeatureList?: boolean;
  showProgress?: boolean;
  showEarlyAccessCTA?: boolean;
  showHeaderBadge?: boolean;
  showBottomCTA?: boolean;
  
  // CTA Props
  ctaTitle?: string;
  ctaDescription?: string;
  primaryCTA?: CTAButton;
  secondaryCTA?: CTAButton;
  
  // Display Props
  columns?: {
    desktop: number;
    tablet: number;
    mobile: number;
  };
  showTrustSignals?: boolean;
  
  // Style Props
  className?: string;
  variant?: 'homepage' | 'roadmap' | 'announcement' | 'mobileLight';
  
  // Animation Props
  enableAnimations?: boolean;
  staggerDelay?: number;
  
  // Behavior Props
  onEarlyAccessClick?: (feature: MultimodalFeature) => void;
}

// Progress Bar Component
const ProgressBar: React.FC<{ 
  percentage: number; 
  color: string; 
  showLabel?: boolean; 
  size?: 'sm' | 'md' | 'lg';
}> = ({ percentage, color, showLabel = true, size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-1',
    md: 'h-2', 
    lg: 'h-3'
  };

  return (
    <div className="w-full space-y-1">
      <div className={cn("bg-gray-200 rounded-full", sizeClasses[size])}>
        <div 
          className={cn(`rounded-full transition-all duration-1000`, sizeClasses[size])}
          style={{ 
            width: `${percentage}%`,
            backgroundColor: `var(--${color})`
          }}
        />
      </div>
      {showLabel && (
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{percentage}% Complete</span>
        </div>
      )}
    </div>
  );
};

// Feature Card Component
const FeatureCard: React.FC<{ 
  feature: MultimodalFeature; 
  showProgress: boolean; 
  showReleaseTimeline: boolean;
  showEarlyAccessCTA: boolean;
  onEarlyAccessClick?: (feature: MultimodalFeature) => void;
  enableAnimations: boolean;
  compact?: boolean;
  itemVariants?: Variants;
}> = ({
  feature,
  showProgress,
  showReleaseTimeline,
  showEarlyAccessCTA,
  onEarlyAccessClick,
  enableAnimations,
  compact = false,
  itemVariants,
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: motionDurations.entrance, ease: motionEasings.premium }
    },
    hover: { 
      y: -8, 
      scale: 1.01,
      transition: { duration: motionDurations.micro }
    }
  };

  const handleEarlyAccessClick = () => {
    if (onEarlyAccessClick) {
      onEarlyAccessClick(feature);
    } else if (feature.earlyAccessUrl) {
      window.location.href = feature.earlyAccessUrl;
    }
  };

  const MotionCard = enableAnimations ? motion.div : 'div';

  // Extract color base from gradient
  const colorBase = feature.gradientFrom.split('-')[0];

  const entranceVariants = itemVariants ?? cardVariants;

  return (
    <MotionCard
      variants={enableAnimations ? entranceVariants : undefined}
      initial={enableAnimations ? 'hidden' : undefined}
      whileInView={enableAnimations ? 'visible' : undefined}
      viewport={enableAnimations ? motionViewport : undefined}
      className="group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <HoverLift liftAmount={6}>
          <Card className={cn(
        "h-full transition-all duration-300 hover:shadow-xl",
        `bg-gradient-to-br from-${colorBase}-50 to-${colorBase}-100`,
        `border-${feature.borderColor} hover:shadow-xl`
      )}>
        <CardHeader className={cn('text-center pb-4', compact && 'pb-2')}>
          <div className="mb-4 w-full">
            <MultimodalScene
              featureId={feature.id}
              compact={compact}
              className="w-full"
              isHovered={isHovered}
            />
          </div>
          <GradientShift colors={["from-purple-600", "to-indigo-600"]}>
            <CardTitle className={compact ? "text-lg" : "text-xl"}>{feature.name}</CardTitle>
          </GradientShift>
          
          {showReleaseTimeline && feature.releaseQuarter && (
            <div className="space-y-2">
              <Badge className={cn(
                "text-xs font-medium px-2 py-1 border",
                `bg-${colorBase}-100 text-${colorBase}-700 border-${feature.borderColor}`
              )}>
                {feature.releaseQuarter}
              </Badge>
            </div>
          )}
        </CardHeader>
        
        <CardContent className="space-y-4">
          <CardDescription className="text-center leading-relaxed">
            {feature.description}
          </CardDescription>
          
          {/* Capabilities List */}
          <div className={cn("space-y-2 text-sm", compact && "space-y-1 text-xs")}>
            {feature.capabilities.map((capability, index) => (
              <div key={index} className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className={cn("w-4 h-4 shrink-0", `text-${feature.accentColor}`, compact && "w-3 h-3")} />
                {capability}
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          {showProgress && feature.progressPercentage && (
            <div className="pt-2">
              <ProgressBar 
                percentage={feature.progressPercentage} 
                color={feature.accentColor}
                size="sm"
              />
            </div>
          )}

          {feature.id === 'visual-memory' && feature.earlyAccessUrl && (
            <div className="pt-2 text-center">
              <Link
                to={feature.earlyAccessUrl}
                className="text-sm font-medium text-orange-700 underline underline-offset-2"
              >
                {t('multimodal.imagesEarlyAccess', { ns: 'homepage' })}
              </Link>
            </div>
          )}
          {feature.id === 'video-memory' && feature.earlyAccessUrl && (
            <div className="pt-2 text-center">
              <Link
                to={feature.earlyAccessUrl}
                className="text-sm font-medium text-orange-700 underline underline-offset-2"
              >
                {t('multimodal.videoEarlyAccess', { ns: 'homepage' })}
              </Link>
            </div>
          )}

          {/* Early Access CTA */}
          {showEarlyAccessCTA && feature.id !== 'visual-memory' && feature.id !== 'video-memory' && (
            <div className="pt-2">
              <PulseGlow color={feature.accentColor} intensity={0.3}>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "w-full transition-colors",
                    `border-${feature.borderColor} text-${colorBase}-700 hover:bg-${colorBase}-100`
                  )}
                  onClick={handleEarlyAccessClick}
                >
                  <Bell className="w-4 h-4 mr-2" />
                  {t('multimodal.getEarlyAccess', { ns: 'homepage' })}
                </Button>
              </PulseGlow>
            </div>
          )}
        </CardContent>
      </Card>
      </HoverLift>
    </MotionCard>
  );
};

const MultimodalPreview: React.FC<MultimodalPreviewProps> = ({
  title,
  subtitle,
  badgeText,
  features = [],
  showReleaseTimeline = true,
  showFeatureList = true,
  showProgress = true,
  showEarlyAccessCTA = true,
  showHeaderBadge = true,
  showBottomCTA = true,
  showTrustSignals = true,
  ctaTitle,
  ctaDescription,
  primaryCTA,
  secondaryCTA,
  columns = { desktop: 3, tablet: 2, mobile: 1 },
  variant = 'homepage',
  enableAnimations = true,
  staggerDelay = 0.2,
  onEarlyAccessClick,
  className = "",
  ...props
}) => {
  const { t } = useTranslation();

  const isMobileLight = variant === 'mobileLight';
  const isHomepageMarketing = variant === 'homepage' || variant === 'mobileLight';
  const resolvedShowReleaseTimeline = showReleaseTimeline;
  const resolvedShowProgress = isHomepageMarketing ? false : showProgress;
  const resolvedShowEarlyAccessCTA = isHomepageMarketing ? false : showEarlyAccessCTA;
  const resolvedShowHeaderBadge = isHomepageMarketing ? false : showHeaderBadge;
  const resolvedShowBottomCTA = isMobileLight ? false : showBottomCTA;
  const resolvedShowTrustSignals = isMobileLight ? false : showTrustSignals;
  const resolvedShowCtaDescription = isHomepageMarketing ? false : true;
  const resolvedShowSecondaryCTA = isHomepageMarketing ? false : true;
  const resolvedColumns = isMobileLight
    ? { desktop: 1, tablet: 1, mobile: 1 }
    : columns;

  // Default content with i18n
  const defaultTitle = title || (
    isMobileLight
      ? t('multimodal.mobileTitle', { ns: 'homepage' })
      : t('multimodal.title', { ns: 'homepage' })
  );
  const defaultSubtitle = subtitle || (
    isMobileLight
      ? t('multimodal.mobileSubtitle', { ns: 'homepage' })
      : t('multimodal.description', { ns: 'homepage' })
  );
  const defaultBadgeText = badgeText || t('multimodal.comingSoon', { ns: 'homepage' });
  const defaultCtaTitle = ctaTitle || t('multimodal.cta.title', { ns: 'homepage' });
  const defaultCtaDescription = ctaDescription || t('multimodal.cta.description', { ns: 'homepage' });
  
  const defaultPrimaryCTA: CTAButton = primaryCTA || {
    text: t('multimodal.cta.startFree', { ns: 'homepage' }),
    href: '/signup'
  };
  
  const defaultSecondaryCTA: CTAButton = secondaryCTA || {
    text: t('multimodal.cta.viewRoadmap', { ns: 'homepage' }),
    href: '/roadmap'
  };

  // Animation variants — section stays visible; only children stagger in
  const previewVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerDelay ?? motionStagger.cards,
        delayChildren: 0.15,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: motionDurations.entrance, ease: motionEasings.premium }
    }
  };

  const gridStaggerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerDelay ?? motionStagger.cards,
      },
    },
  };

  // Variant classes
  const variantClasses = {
    homepage: "space-y-8 py-16 bg-gradient-to-br from-purple-50 to-indigo-50",
    roadmap: "space-y-6 py-12 bg-background",
    announcement: "space-y-10 py-20 bg-gradient-to-br from-primary/5 to-primary/10",
    mobileLight: "space-y-6 py-10 px-4 bg-gradient-to-br from-purple-50 to-indigo-50",
  };

  const gridClasses = cn(
    'grid gap-4 md:gap-8 max-w-6xl mx-auto px-4 md:px-0',
    resolvedColumns.mobile === 1 && 'grid-cols-1',
    resolvedColumns.tablet === 2 && 'md:grid-cols-2',
    resolvedColumns.desktop === 3 && 'lg:grid-cols-3',
    resolvedColumns.desktop === 1 && 'lg:grid-cols-1',
  );

  // Motion components
  const MotionSection = enableAnimations ? motion.section : 'section';
  const MotionDiv = enableAnimations ? motion.div : 'div';

  // Handle CTA clicks
  const handlePrimaryCTAClick = () => {
    if (defaultPrimaryCTA.onClick) {
      defaultPrimaryCTA.onClick();
    } else {
      window.location.href = defaultPrimaryCTA.href;
    }
  };

  const handleSecondaryCTAClick = () => {
    if (defaultSecondaryCTA.onClick) {
      defaultSecondaryCTA.onClick();
    } else {
      window.location.href = defaultSecondaryCTA.href;
    }
  };

  return (
    <MotionSection 
      variants={enableAnimations ? previewVariants : undefined}
      initial={enableAnimations ? "hidden" : undefined}
      whileInView={enableAnimations ? "visible" : undefined}
      viewport={enableAnimations ? motionViewport : undefined}
      className={cn(variantClasses[variant], className)}
      {...props}
    >
      {/* Section Header */}
      <motion.div
        className="text-center space-y-8"
        initial={enableAnimations ? { opacity: 0, y: 30 } : undefined}
        whileInView={enableAnimations ? { opacity: 1, y: 0 } : undefined}
        viewport={enableAnimations ? motionViewport : undefined}
        transition={enableAnimations ? { duration: motionDurations.entrance, ease: motionEasings.premium } : undefined}
      >
        <motion.div
          initial={enableAnimations ? { scale: 0.9, opacity: 0 } : undefined}
          whileInView={enableAnimations ? { scale: 1, opacity: 1 } : undefined}
          viewport={enableAnimations ? motionViewport : undefined}
          transition={enableAnimations ? { duration: motionDurations.entranceFast, ease: motionEasings.premium } : undefined}
          className={cn(
            "inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-100 via-indigo-100 to-purple-100 rounded-full text-sm font-semibold text-purple-700 mb-6 shadow-lg border border-purple-200/50",
            !resolvedShowHeaderBadge && "hidden"
          )}
        >
          <Sparkles className="w-5 h-5" />
          {defaultBadgeText}
        </motion.div>

        <motion.div
          initial={enableAnimations ? { opacity: 0, y: 20 } : undefined}
          whileInView={enableAnimations ? { opacity: 1, y: 0 } : undefined}
          viewport={enableAnimations ? motionViewport : undefined}
          transition={enableAnimations ? { duration: motionDurations.entrance, ease: motionEasings.premium, delay: 0.1 } : undefined}
        >
          <h2 className={cn(
            "font-bold text-foreground leading-tight",
            isMobileLight ? "text-2xl" : "text-4xl md:text-5xl lg:text-6xl"
          )}>
            {isMobileLight ? (
              <>
                <span className="text-gray-900">{defaultTitle}</span>
              </>
            ) : defaultTitle.includes(':') ? (
              <>
                <span className="text-gray-900">
                  {defaultTitle.split(':')[0]}:
                </span>
                <span className="block">
                  <GradientShift colors={["from-purple-600", "to-indigo-600"]}>
                    {defaultTitle.split(':').slice(1).join(':').trim()}
                  </GradientShift>
                </span>
              </>
            ) : (
              <span className="text-gray-900">{defaultTitle}</span>
            )}
          </h2>
          <motion.p
            className={cn(
              "text-muted-foreground max-w-4xl mx-auto leading-relaxed font-light",
              isMobileLight ? "text-base mt-3" : "text-xl md:text-2xl mt-6"
            )}
            initial={enableAnimations ? { opacity: 0, y: 20 } : undefined}
            whileInView={enableAnimations ? { opacity: 1, y: 0 } : undefined}
            viewport={enableAnimations ? motionViewport : undefined}
            transition={enableAnimations ? { duration: motionDurations.entrance, ease: motionEasings.premium, delay: 0.15 } : undefined}
          >
            {defaultSubtitle}
          </motion.p>
        </motion.div>
      </motion.div>

      {/* Feature Cards Grid */}
      {features.length > 0 && (
        <MotionDiv
          className={gridClasses}
          variants={enableAnimations ? gridStaggerVariants : undefined}
          initial={enableAnimations ? 'hidden' : undefined}
        >
          {features.map((feature) => (
            <FeatureCard
              key={feature.id}
              feature={feature}
              showProgress={resolvedShowProgress}
              showReleaseTimeline={resolvedShowReleaseTimeline}
              showEarlyAccessCTA={resolvedShowEarlyAccessCTA}
              onEarlyAccessClick={onEarlyAccessClick}
              enableAnimations={enableAnimations}
              compact={isMobileLight}
              itemVariants={itemVariants}
            />
          ))}
        </MotionDiv>
      )}

      {/* Premium CTA Section */}
      {resolvedShowBottomCTA && (
      <MotionDiv
        variants={enableAnimations ? itemVariants : undefined}
        className="text-center space-y-8 pt-12"
      >
        <motion.div
          className="max-w-4xl mx-auto"
          initial={enableAnimations ? { opacity: 0, y: 30 } : undefined}
          whileInView={enableAnimations ? { opacity: 1, y: 0 } : undefined}
          viewport={enableAnimations ? motionViewport : undefined}
          transition={enableAnimations ? { duration: motionDurations.entrance, ease: motionEasings.premium } : undefined}
        >
          <motion.h3
            className="text-3xl md:text-4xl font-bold text-foreground mb-6 leading-tight"
            initial={enableAnimations ? { opacity: 0, scale: 0.96 } : undefined}
            whileInView={enableAnimations ? { opacity: 1, scale: 1 } : undefined}
            viewport={enableAnimations ? motionViewport : undefined}
            transition={enableAnimations ? { duration: motionDurations.entrance, ease: motionEasings.premium, delay: 0.1 } : undefined}
          >
            <GradientShift colors={["from-purple-600", "to-indigo-600"]}>
              {defaultCtaTitle}
            </GradientShift>
          </motion.h3>
          {resolvedShowCtaDescription && (
            <motion.p
              className="text-xl text-muted-foreground mb-8 leading-relaxed font-light"
              initial={enableAnimations ? { opacity: 0, y: 20 } : undefined}
              whileInView={enableAnimations ? { opacity: 1, y: 0 } : undefined}
              viewport={enableAnimations ? motionViewport : undefined}
              transition={enableAnimations ? { duration: motionDurations.entrance, ease: motionEasings.premium, delay: 0.15 } : undefined}
            >
              {defaultCtaDescription}
            </motion.p>
          )}

          <motion.div
            className={cn(
              'flex flex-col sm:flex-row gap-6 justify-center',
              !resolvedShowCtaDescription && 'mt-0'
            )}
            initial={enableAnimations ? { opacity: 0, y: 20 } : undefined}
            whileInView={enableAnimations ? { opacity: 1, y: 0 } : undefined}
            viewport={enableAnimations ? motionViewport : undefined}
            transition={enableAnimations ? { duration: motionDurations.entranceFast, ease: motionEasings.premium, delay: 0.2 } : undefined}
          >
            <PulseGlow color="purple" intensity={0.4}>
              <HoverLift liftAmount={4}>
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: motionDurations.micro }}
                >
                  <Button
                    variant="sanctuary"
                    size="lg"
                    onClick={handlePrimaryCTAClick}
                    className="h-14 px-10 text-lg font-bold bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-xl hover:shadow-2xl transition-all duration-300"
                  >
                    {defaultPrimaryCTA.text}
                    <Sparkles className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>
              </HoverLift>
            </PulseGlow>
            {resolvedShowSecondaryCTA && (
            <HoverLift liftAmount={4}>
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: motionDurations.micro }}
              >
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleSecondaryCTAClick}
                  className="h-14 px-10 text-lg font-bold border-2 border-purple-300 text-purple-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {defaultSecondaryCTA.text}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </HoverLift>
            )}
          </motion.div>
        </motion.div>

        {/* Trust Signals */}
        {resolvedShowTrustSignals && (
          <div className="flex flex-wrap justify-center gap-8 pt-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-indigo-600" />
              {t('multimodal.trustSignals.multiModalAI', { ns: 'homepage' })}
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-blue-600" />
              {t('multimodal.trustSignals.enterpriseSecurity', { ns: 'homepage' })}
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-orange-600" />
              {t('multimodal.trustSignals.realTimeProcessing', { ns: 'homepage' })}
            </div>
          </div>
        )}
      </MotionDiv>
      )}
    </MotionSection>
  );
};

export default MultimodalPreview;
export type { MultimodalPreviewProps, MultimodalFeature, FeatureStatus, CTAButton };
